import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { routesApi } from '../store/api/routesApi';
import { useGetApplicationsQuery } from '../store/api/applicationsApi';
import type { Route } from '../types';
/**
 * Заявки клиента и объединённый список маршрутов по этим заявкам (без изменений backend).
 */
export function useContractorClientRelated(clientId: number | undefined, enabled: boolean) {
  const dispatch = useDispatch<AppDispatch>();

  const { data: appsResp, isLoading: appsLoading, isError: appsError } = useGetApplicationsQuery(
    { clientId: clientId!, page: 1, limit: 500 },
    { skip: !enabled || !clientId }
  );

  const applications = useMemo(
    () => appsResp?.applications ?? [],
    [appsResp?.applications]
  );
  const applicationIdsKey = useMemo(() => applications.map((a) => a.id).join(','), [applications]);

  const [routes, setRoutes] = useState<Route[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);

  useEffect(() => {
    const apps = appsResp?.applications ?? [];
    if (!enabled || !clientId || apps.length === 0) {
      setRoutes([]);
      setRoutesLoading(false);
      return;
    }

    let cancelled = false;
    setRoutesLoading(true);

    (async () => {
      try {
        const byId = new Map<number, Route>();
        for (const app of apps) {
          if (cancelled) return;
          const result = await dispatch(routesApi.endpoints.getRoutesByApplication.initiate(app.id));
          const list = (result as { data?: Route[] }).data ?? [];
          for (const route of list) {
            if (!byId.has(route.id)) byId.set(route.id, route);
          }
        }
        if (cancelled) return;
        setRoutes(Array.from(byId.values()).sort((a, b) => b.id - a.id));
      } finally {
        if (!cancelled) setRoutesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, clientId, applicationIdsKey, dispatch, appsResp]);

  return {
    applications,
    routes,
    loading: appsLoading || routesLoading,
    applicationsError: appsError
  };
}
